import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Search, FlaskConical } from "lucide-react";
import { usePerms } from "@/context/PermContext";

const CATEGORIES = ["ALL", "X-RAY", "USG", "CT", "MRI", "ECG", "ECHO", "LAB", "PATHOLOGY", "OTHER"];
const CAT_COLOR = {
  "X-RAY": "bg-blue-100 text-blue-800",
  USG: "bg-teal-100 text-teal-800",
  CT: "bg-purple-100 text-purple-800",
  MRI: "bg-indigo-100 text-indigo-800",
  ECG: "bg-amber-100 text-amber-800",
  ECHO: "bg-orange-100 text-orange-800",
  LAB: "bg-emerald-100 text-emerald-800",
  PATHOLOGY: "bg-rose-100 text-rose-800",
  OTHER: "bg-slate-100 text-slate-700",
};

const empty = { code: "", name: "", category: "LAB", description: "", rate: 0, sample_type: "", tat_hours: 24, active: true };

export default function InvestigationMaster() {
  const { can } = usePerms();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("ALL");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    api.get("/investigation-master", { params: { q, category: cat === "ALL" ? "" : cat } }).then((r) => setRows(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, cat]);

  const canWrite = can("investigations", "edit") || can("settings", "edit");

  const openNew = () => { setForm(empty); setEditingId(null); setDlgOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEditingId(r.id); setDlgOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editingId) await api.put(`/investigation-master/${editingId}`, form);
      else await api.post("/investigation-master", form);
      toast.success(editingId ? "Updated" : "Added");
      setDlgOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete '${r.name}'? This will not affect existing OPD entries.`)) return;
    try { await api.delete(`/investigation-master/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Investigation Master</h1>
          <p className="text-sm text-slate-500">X-Ray · USG · CT · MRI · ECG · Echo · Lab / Pathology catalog with rates. Feeds OPD investigation billing.</p>
        </div>
        {canWrite && <Button data-testid="inv-new-btn" onClick={openNew} className="bg-blue-700 hover:bg-blue-800 rounded-sm"><Plus className="w-4 h-4 mr-1"/> New Investigation</Button>}
      </div>

      <div className="bg-white border p-3 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
          <Input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, code or description" className="pl-7 h-9"/>
        </div>
        <div className="flex gap-1 flex-wrap">
          {CATEGORIES.map((c) => (
            <button key={c} data-testid={`inv-cat-${c}`} onClick={() => setCat(c)}
              className={`h-9 px-3 text-xs border rounded-sm ${cat === c ? "border-blue-700 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 text-slate-600"}`}>{c}</button>
          ))}
        </div>
      </div>

      <div className="bg-white border">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left">
            <th className="label-tiny p-2 w-24">Code</th>
            <th className="label-tiny">Name</th>
            <th className="label-tiny w-28">Category</th>
            <th className="label-tiny">Description</th>
            <th className="label-tiny w-32">Sample / TAT</th>
            <th className="label-tiny text-right w-24">Rate</th>
            <th className="label-tiny w-16"></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b hover:bg-blue-50/40" data-testid={`inv-row-${r.id}`}>
                <td className="p-2 font-mono text-xs">{r.code || "-"}</td>
                <td className="font-medium">{r.name}</td>
                <td><Badge className={`rounded-sm text-[10px] ${CAT_COLOR[r.category] || "bg-slate-100"}`}>{r.category}</Badge></td>
                <td className="text-xs text-slate-600">{r.description || "-"}</td>
                <td className="text-xs text-slate-500">{r.sample_type ? `${r.sample_type} · ` : ""}{r.tat_hours}h</td>
                <td className="text-right font-mono">{fmtINR(r.rate)}</td>
                <td>
                  {canWrite && (
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(r)} data-testid={`inv-edit-${r.id}`} className="p-1 text-blue-700 hover:bg-blue-50 rounded-sm"><Edit2 className="w-3.5 h-3.5"/></button>
                      <button onClick={() => remove(r)} data-testid={`inv-del-${r.id}`} className="p-1 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500"><FlaskConical className="w-8 h-8 mx-auto text-slate-300 mb-2"/>No investigations match this filter</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit Investigation" : "New Investigation"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code</Label><Input data-testid="inv-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. XR-CHEST"/></div>
              <div><Label>Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-9 border border-slate-300 w-full px-2 text-sm rounded-sm" data-testid="inv-form-cat">
                  {CATEGORIES.filter((c) => c !== "ALL").map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-span-2"><Label>Name*</Label><Input data-testid="inv-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. X-RAY CHEST PA"/></div>
              <div className="col-span-2"><Label>Description</Label><Textarea data-testid="inv-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}/></div>
              <div><Label>Rate (₹)*</Label><Input type="number" data-testid="inv-form-rate" value={form.rate} onChange={(e) => setForm({ ...form, rate: parseFloat(e.target.value) || 0 })}/></div>
              <div><Label>TAT (hours)</Label><Input type="number" data-testid="inv-form-tat" value={form.tat_hours} onChange={(e) => setForm({ ...form, tat_hours: parseInt(e.target.value) || 0 })}/></div>
              <div className="col-span-2"><Label>Sample Type (LAB)</Label><Input data-testid="inv-form-sample" value={form.sample_type} onChange={(e) => setForm({ ...form, sample_type: e.target.value })} placeholder="e.g. EDTA blood, Serum, Urine"/></div>
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}/> Active (available for selection)</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button>
            <Button data-testid="inv-form-save" onClick={save} className="bg-blue-700 hover:bg-blue-800">{editingId ? "Update" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
